import React, { useState } from 'react';
import MuscleAnimation from './MuscleAnimation';
import exercisesData from '../../data/exercises.json';

const AnimationDemo = () => {
  const [selectedExercise, setSelectedExercise] = useState(exercisesData[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);

  // Get a few sample exercises for demo
  const sampleExercises = exercisesData.slice(0, 5);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6 text-center">Muscle Animation System Demo</h2>
      
      {/* Exercise selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Exercise:
        </label>
        <select
          value={selectedExercise.id}
          onChange={(e) => {
            const exercise = exercisesData.find(ex => ex.id === e.target.value);
            setSelectedExercise(exercise);
            setIsPlaying(false);
          }}
          className="w-full p-2 border border-gray-300 rounded-md"
        >
          {sampleExercises.map((exercise) => (
            <option key={exercise.id} value={exercise.id}>
              {exercise.name}
            </option>
          ))}
        </select>
      </div>

      {/* Exercise details */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-semibold mb-2">{selectedExercise.name}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <strong>Primary Muscles:</strong> {selectedExercise.primaryMuscles?.join(', ') || 'None'}
          </div>
          <div>
            <strong>Secondary Muscles:</strong> {selectedExercise.secondaryMuscles?.join(', ') || 'None'}
          </div>
          <div>
            <strong>Category:</strong> {selectedExercise.category}
          </div>
          <div>
            <strong>Level:</strong> {selectedExercise.level}
          </div>
        </div>
      </div>

      {/* Animation controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-center justify-center">
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className={`px-6 py-2 rounded-md font-medium ${
            isPlaying 
              ? 'bg-red-500 hover:bg-red-600 text-white' 
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
        >
          {isPlaying ? 'Stop Animation' : 'Start Animation'}
        </button>
        
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Speed:</label>
          <input
            type="range"
            min="0.5"
            max="3"
            step="0.5"
            value={speed}
            onChange={(e) => setSpeed(parseFloat(e.target.value))}
            className="w-20"
          />
          <span className="text-sm text-gray-600">{speed}x</span>
        </div>
      </div>

      {/* Animation display */}
      <div className="flex justify-center">
        <MuscleAnimation
          exercise={selectedExercise}
          isPlaying={isPlaying}
          speed={speed}
        />
      </div>

      {/* Instructions */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-semibold mb-2">Exercise Instructions:</h4>
        <ol className="list-decimal list-inside space-y-1 text-sm">
          {selectedExercise.instructions?.map((instruction, index) => (
            <li key={index}>{instruction}</li>
          ))}
        </ol>
      </div>

      {/* Technical details */}
      <div className="mt-6 p-4 bg-gray-100 rounded-lg">
        <h4 className="font-semibold mb-2">Technical Implementation:</h4>
        <ul className="text-sm space-y-1">
          <li>• <strong>SVG-based:</strong> Scalable vector graphics for crisp animations</li>
          <li>• <strong>Muscle mapping:</strong> Primary muscles (blue), Secondary muscles (green)</li>
          <li>• <strong>Animation phases:</strong> Based on exercise instructions and movement patterns</li>
          <li>• <strong>Responsive:</strong> Adapts to different screen sizes</li>
          <li>• <strong>Customizable:</strong> Colors, speed, and animation patterns can be adjusted</li>
        </ul>
      </div>
    </div>
  );
};

export default AnimationDemo;

